import React from "react";

/**
 * Reusable service detail page layout — matches the WebDev page structure.
 *
 * Props:
 *  title        {string}   — page heading
 *  crumb        {string}   — breadcrumb label
 *  heroBg       {string}   — bg image path for sticky side panel
 *  overview     {string[]} — array of overview paragraph strings
 *  benefits     {Array<{title, desc}>}
 *  accordionId  {string}   — unique id prefix for accordion (avoids id collisions)
 *  included     {Array<{title, body}>}  — "What's included" accordion items
 *  techIcons    {string[]} — icon numbers e.g. ['01','02']
 *  steps        {Array<{num, title, desc}>}
 *  projects     {Array<{img, logo, title, stat, statLabel}>}
 *  testimonials {Array<{stars, text, avatar, name, role}>}
 */
function ServicePage({
  title,
  crumb,
  heroBg,
  overview = [],
  benefits = [],
  accordionId = "accordionIncluded",
  included = [],
  techIcons = [],
  steps = [],
  projects = [],
  testimonials = [],
}) {
  return (
    <main>

      {/* ===================== HERO ===================== */}
      <section className="position-relative pt-sm-8 pt-lg-9 pb-0 overflow-hidden mb-n6">
        <div className="bg-secondary-grad h-500px w-100 position-absolute bottom-0 start-0">
          <span>
            <svg className="mt-n2" viewBox="0 0 1950 135" style={{ enableBackground: "new 0 0 1950 135" }}>
              <path className="fill-body" d="M1480.3,110.5c238.7,18.9,359.6-42.2,469.7-80.7V1.7L0,0v129.4c57.3-41.7,287.7-15.9,446.4,0 c170.6,17.1,342.3-15.8,440.8-35.8C1104,49.6,1274.8,94.2,1480.3,110.5z" />
            </svg>
          </span>
        </div>

        <div className="container position-relative" data-sticky-container>
          <div className="row">

            <div className="col-md-6">
              <nav className="mb-2" aria-label="breadcrumb">
                <ol className="breadcrumb pt-0">
                  <li className="breadcrumb-item"><a href="/">Home</a></li>
                  <li className="breadcrumb-item"><a href="/services">Services</a></li>
                  <li className="breadcrumb-item active" aria-current="page">{crumb}</li>
                </ol>
              </nav>

              <h1 className="display-5 mb-5">{title}</h1>
              <a className="btn btn-dark icon-link icon-link-hover" href="/contact">
                Start a project<i className="bi bi-arrow-right"></i>
              </a>

              <div className="mt-5 mt-md-9">
                <h5>Overview</h5>
                {overview.map((p, i) => <p key={i}>{p}</p>)}
              </div>
            </div>

            <div className="col-md-5 ms-auto">
              <div data-sticky data-margin-top="100" data-sticky-for="576">
                <div
                  className="rounded-4 h-400px h-lg-500px"
                  style={{ background: `url(${heroBg}) no-repeat`, backgroundSize: "cover", backgroundPosition: "center" }}
                ></div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ===================== KEY BENEFITS ===================== */}
      <section className="bg-secondary-grad overflow-hidden mt-6 pt-7">
        <div className="container pb-8">

          <h5 className="mb-4">Key benefits</h5>
          <div className="row row-cols-1 row-cols-sm-2 row-cols-xl-4 g-4">
            {benefits.map((b, i) => (
              <div className="col" key={i}>
                <div className="card card-body h-100 rounded-3 p-4">
                  <h6 className="mb-3">{b.title}</h6>
                  <p className="mb-0">{b.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* ---- What's included ---- */}
          <div className="row g-4 mt-5 mt-md-7">
            <div className="col-lg-6 pe-lg-6">
              <img src="assets/images/services/01.jpg" className="rounded-4 w-100" alt="service" />
            </div>

            <div className="col-lg-6">
              <h2>What's included?</h2>
              <div className="accordion accordion-icon accordion-icon-start mt-4" id={accordionId}>
                {included.map((item, idx) => (
                  <div className="accordion-item mb-2" key={idx}>
                    <h6 className="accordion-header" id={`${accordionId}-h-${idx}`}>
                      <button
                        className={`accordion-button py-2${idx === 0 ? "" : " collapsed"}`}
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target={`#${accordionId}-c-${idx}`}
                        aria-expanded={idx === 0}
                        aria-controls={`${accordionId}-c-${idx}`}
                      >
                        <span className="lead fw-bold">{item.title}</span>
                      </button>
                    </h6>
                    <div
                      id={`${accordionId}-c-${idx}`}
                      className={`accordion-collapse collapse${idx === 0 ? " show" : ""}`}
                      aria-labelledby={`${accordionId}-h-${idx}`}
                      data-bs-parent={`#${accordionId}`}
                    >
                      <div className="accordion-body">{item.body}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ---- Technologies ---- */}
          {techIcons.length > 0 && (
            <div className="inner-container text-center mt-7">
              <h4>Technologies used</h4>
              <ul className="list-inline d-flex justify-content-center flex-wrap gap-4 mt-4">
                {techIcons.map((n) => (
                  <li className="list-inline-item me-0" key={n}>
                    <a href="#" className="icon-xl btn-transition bg-body d-flex justify-content-center align-items-center rounded-2">
                      <img src={`assets/images/client/icons/${n}.svg`} className="h-40px" alt="tech icon" />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

        </div>
      </section>

      {/* ===================== PROCESS ===================== */}
      <section className="pt-0 mt-n9">
        <div className="container-fluid position-relative">
          <div className="max-width-1550 bg-dark position-relative rounded-4 overflow-hidden py-5 py-sm-6 py-lg-8" data-bs-theme="dark">
            <div className="container position-relative">
              <h2 className="mb-4 mb-lg-6">Step-by-Step Process</h2>
              <div className="row row-cols-1 row-cols-md-2 row-cols-lg-4 g-4">
                {steps.map((s) => (
                  <div className="col" key={s.num}>
                    <h4 className="text-primary-grad">{s.num}</h4>
                    <h6>{s.title}</h6>
                    <p className="mb-0">{s.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== PORTFOLIO SHOWCASE ===================== */}
      {projects.length > 0 && (
        <section className="pt-0">
          <div className="container">
            <div className="row g-4">

              <div className="col-lg-4 text-center text-lg-start">
                <h2 className="mb-lg-4">Showcase of previous projects</h2>
                <p>Explore our diverse portfolio showcasing the quality of our work.</p>
                <div className="d-flex justify-content-center justify-content-lg-start gap-3 mt-lg-4">
                  <a href="#" className="btn btn-secondary btn-lg btn-icon rounded-circle mb-0 swiper-button-prev-team">
                    <i className="bi bi-arrow-left"></i>
                  </a>
                  <a href="#" className="btn btn-secondary btn-lg btn-icon rounded-circle mb-0 swiper-button-next-team">
                    <i className="bi bi-arrow-right"></i>
                  </a>
                </div>
              </div>

              <div className="col-lg-8 ps-xl-6">
                <div className="swiper" data-swiper-options='{"spaceBetween":30,"autoplay":false,"navigation":{"nextEl":".swiper-button-next-team","prevEl":".swiper-button-prev-team"}}'>
                  <div className="swiper-wrapper">
                    {projects.map((p, i) => (
                      <div className="swiper-slide" key={i}>
                        <div className="card bg-secondary bg-opacity-50 rounded-4 overflow-hidden mb-3">
                          <div className="row g-0">
                            <div className="col-md-5">
                              <img src={p.img} className="img-fluid rounded-start h-100 object-fit-cover" alt="project" />
                            </div>
                            <div className="col-md-7">
                              <div className="card-body d-flex flex-column align-items-start h-100 p-4">
                                <h6 className="card-title">{p.title}</h6>
                                <p className="card-text">{p.desc}</p>
                                <div className="d-sm-flex justify-content-between align-items-center mt-auto w-100">
                                  <div className="mb-3 mb-sm-0">
                                    <h4 className="text-primary-grad mb-0">{p.stat}</h4>
                                    <small>{p.statLabel}</small>
                                  </div>
                                  <a href="#" className="btn btn-sm btn-dark stretched-link mb-0">View detail</a>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>
      )}

      {/* ===================== TESTIMONIALS ===================== */}
      <section className="bg-body position-relative py-0">
        <div className="container position-relative z-index-9">

          <div className="row align-items-center mb-4 mb-md-5">
            <div className="col-md-7 col-xl-5">
              <h2 className="mb-0">Hear from our happy customers</h2>
            </div>
            <div className="col-md-5 ms-sm-auto text-sm-end mt-5 mt-sm-0">
              <div className="d-flex gap-2 justify-content-sm-end">
                <a href="#" className="btn btn-primary-grad btn-icon btn-lg rounded-circle mb-0 swiper-button-prev">
                  <i className="bi bi-arrow-left"></i>
                </a>
                <a href="#" className="btn btn-primary-grad btn-icon btn-lg rounded-circle mb-0 swiper-button-next">
                  <i className="bi bi-arrow-right"></i>
                </a>
              </div>
            </div>
          </div>

          <div className="swiper" data-swiper-options='{"spaceBetween":30,"breakpoints":{"576":{"slidesPerView":1},"768":{"slidesPerView":2},"992":{"slidesPerView":3}},"navigation":{"nextEl":".swiper-button-next","prevEl":".swiper-button-prev"}}'>
            <div className="swiper-wrapper">
              {testimonials.map((t, i) => (
                <div className="swiper-slide" key={i}>
                  <div className="card bg-secondary bg-opacity-50 bg-blur rounded-4 p-4 h-100">
                    <div className="card-body p-0 mb-4">
                      <ul className="list-inline mb-3">
                        {[...Array(Math.floor(t.stars))].map((_, idx) => (
                          <li className="list-inline-item me-0" key={idx}><i className="bi bi-star-fill text-warning"></i></li>
                        ))}
                        {t.stars % 1 !== 0 && <li className="list-inline-item me-0"><i className="bi bi-star-half text-warning"></i></li>}
                      </ul>
                      <blockquote><p className="heading-color mb-0">{t.text}</p></blockquote>
                    </div>
                    <div className="card-footer bg-transparent p-0">
                      <div className="d-flex align-items-center">
                        <div className="avatar flex-shrink-0">
                          <img className="avatar-img rounded-circle" src={`assets/images/avatar/${t.avatar}`} alt="avatar" />
                        </div>
                        <div className="ms-3">
                          <p className="lead heading-color fw-semibold mb-0">{t.name}</p>
                          <small>{t.role}</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* ===================== SAY HELLO / CONTACT FORM ===================== */}
      <section className="pb-0 overflow-hidden">
        <div className="bg-secondary-grad position-relative pb-8">
          <span>
            <svg className="position-absolute top-0 start-0 mt-n3 mt-sm-n1" viewBox="0 0 1920 108" style={{ enableBackground: "new 0 0 1920 108" }}>
              <path className="fill-body" d="M0,0l1920,1.5V108L0,0z" />
            </svg>
          </span>

          <div className="container position-relative pt-5 pt-lg-0">
            <div className="row align-items-center g-4">

              <div className="col-lg-5">
                <span className="hand-wave-animate h2">🖐️</span>
                <h2 className="mb-3 h1">Say Hello</h2>
                <p>Our friendly team is ready to assist you with whatever you need.</p>
                <div className="row row-cols-1 row-cols-sm-2 g-4 mt-3 mt-md-5">
                  <div className="col">
                    <span className="fs-3 text-primary-grad"><i className="bi bi-headset"></i></span>
                    <h6 className="my-2 my-sm-3">Call us</h6>
                    <p className="mb-2">Let's work together — get in touch!</p>
                    <a href="tel:7677607080" className="heading-color hover-underline-animation">+91 76776 07080</a>
                  </div>
                  <div className="col">
                    <span className="fs-3 text-primary-grad"><i className="bi bi-envelope"></i></span>
                    <h6 className="my-2 my-sm-3">Email us</h6>
                    <p className="mb-2">We respond to all inquiries within 24 hours.</p>
                    <a href="mailto:info@arixautomation.com" className="heading-color hover-underline-animation">info@arixautomation.com</a>
                  </div>
                </div>
              </div>

              <div className="col-lg-6 ms-auto mt-5 mt-lg-n7">
                <div className="card card-body rounded-4 shadow-primary-lg p-4">
                  <form className="row form-border-transparent g-3">
                    <div className="col-md-6">
                      <label className="form-label">First name</label>
                      <input type="text" className="form-control bg-secondary" />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Last name</label>
                      <input type="text" className="form-control bg-secondary" />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Email address</label>
                      <input type="email" className="form-control bg-secondary" placeholder="name@example.com" />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Mobile number</label>
                      <input type="text" className="form-control bg-secondary" />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Subject</label>
                      <input type="text" className="form-control bg-secondary" />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Message</label>
                      <textarea className="form-control bg-secondary" style={{ height: "150px" }}></textarea>
                    </div>
                    <div className="col-12 mt-4">
                      <button className="btn btn-primary mb-0">Send a message</button>
                    </div>
                  </form>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

    </main>
  );
}

export default ServicePage;
